import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Chart, registerables, ChartData, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { OAuthService } from '../../services/oauth.service';
import { OAuth } from '../../models/oauth';

interface TimeframeOption {
  label: string;
  days: number | null;
}

// One Instagram story frame — an independent STORY media object — with its insights.
interface StoryFrame {
  mediaId: string;
  mediaType: string;
  postedAt: string | null;
  expiresAt: string | null;
  permalink: string | null;
  thumbnailUrl: string | null;
  analytics: any;
  retentionFromFirst: number | null;
  retentionFromPrevious: number | null;
  exits: number | null;
  exitRate: number | null;
}

// Frames the creator posted together, grouped by our own timestamp window — Instagram has no
// grouping object of its own.
interface StoryGroup {
  groupId: string;
  startedAt: string | null;
  endedAt: string | null;
  expiresAt: string | null;
  frames: StoryFrame[];
  totalViews: number;
  maxFrameReach: number;
  totalReplies: number;
  totalShares: number;
  totalFollows: number;
  totalProfileVisits: number;
  totalInteractions: number;
  navigationTotals: Record<string, number>;
  completion: number | null;
}

@Component({
  selector: 'app-video-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective],
  templateUrl: './video-analytics.component.html',
  styleUrl: './video-analytics.component.css'
})

export class VideoAnalyticsComponent implements OnInit {
  readonly platforms = ['instagram', 'tiktok', 'youtube'];

  readonly timeframes: TimeframeOption[] = [
    { label: 'Last 365 Days', days: 365 },
    { label: 'Last 30 Days',  days: 30  },
    { label: 'Last 7 Days',   days: 7   },
    { label: 'Yesterday',     days: 1   },
  ];
  selectedTimeframeDays: number | null = 365;

  allOAuths: OAuth[] = [];
  filteredOAuths: OAuth[] = [];
  selectedPlatform = '';
  selectedOAuthId = '';
  videoId = '';
  loading = false;
  errorMessage = '';
  result: any = null;
  thumbnailUrl: string | null = null;
  videoUrl: string | null = null;
  fromCache = false;
  lastRefresh: Date | null = null;

  // Instagram only: posts/reels are looked up by link, stories are picked from a live list.
  contentMode: 'post' | 'story' = 'post';
  storiesLoading = false;
  storiesError = '';
  storiesLoaded = false;
  storyGroups: StoryGroup[] = [];
  selectedStoryGroup: StoryGroup | null = null;
  selectedStoryFrame: StoryFrame | null = null;

  get isInstagram(): boolean {
    return this.selectedPlatform === 'instagram';
  }

  // For Instagram the server decides which metrics exist for the media's type, so the card grid
  // is driven entirely by result.metrics. The other platforms keep their fixed grid.
  get metricsDrivenByServer(): boolean {
    return this.isInstagram;
  }

  get timeframeSupported(): boolean {
    return this.selectedPlatform === 'youtube';
  }

  get selectedTimeframeLabel(): string {
    return this.timeframes.find(t => t.days === this.selectedTimeframeDays)?.label ?? '';
  }

  get contentIdLabel(): string {
    return this.isInstagram ? 'Post or reel link' : 'Content ID';
  }

  get contentIdPlaceholder(): string {
    return this.isInstagram
      ? 'https://www.instagram.com/reel/ABC123xyz/'
      : 'Enter video / reel / short ID';
  }

  // Time-series chart
  chartVisible = false;
  selectedTimeSeriesMetric = 'views';
  timeSeriesMetricOptions: string[] = [];
  chartData: ChartData<'line'> = { labels: [], datasets: [] };
  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: '#aaa', maxTicksLimit: 12 }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.05)' } }
    }
  };

  // Frame-to-frame drop-off for the selected story
  storyChartData: ChartData<'line'> = { labels: [], datasets: [] };

  constructor(private oauthService: OAuthService) {
    Chart.register(...registerables);
  }

  ngOnInit(): void {
    this.oauthService.getOAuths().subscribe({
      next: oauths => this.allOAuths = oauths
    });
  }

  onPlatformChange(): void {
    this.selectedOAuthId = '';
    this.videoId = '';
    this.contentMode = 'post';
    this.clearResults();
    this.clearStories();
    this.selectedTimeframeDays = 365;
    this.filteredOAuths = this.allOAuths.filter(o => o.platform === this.selectedPlatform);
  }

  onCreatorChange(): void {
    this.videoId = '';
    this.clearResults();
    this.clearStories();
  }

  setContentMode(mode: 'post' | 'story'): void {
    if (this.contentMode === mode) return;
    this.contentMode = mode;
    this.clearResults();
    if (mode === 'story' && !this.storiesLoaded) this.loadStories();
  }

  private clearResults(): void {
    this.result = null;
    this.errorMessage = '';
    this.thumbnailUrl = null;
    this.videoUrl = null;
    this.chartVisible = false;
    this.fromCache = false;
    this.lastRefresh = null;
  }

  private clearStories(): void {
    this.storiesLoading = false;
    this.storiesError = '';
    this.storiesLoaded = false;
    this.storyGroups = [];
    this.selectedStoryGroup = null;
    this.selectedStoryFrame = null;
    this.storyChartData = { labels: [], datasets: [] };
  }

  // ── Posts and reels ───────────────────────────────────────────────────────────

  onSubmit(forceRefresh = false): void {
    const contentId = this.contentIdForRequest();
    if (!this.selectedOAuthId || !contentId) return;

    this.loading = true;
    this.clearResults();

    const { startDate, endDate } = this.getDateRange();
    this.oauthService.getVideoAnalytics(this.selectedOAuthId, contentId, startDate, endDate, forceRefresh).subscribe({
      next: res => {
        this.loading = false;
        if (res.success === false) {
          this.errorMessage = res.message || 'Video not found.';
        } else {
          this.applyResult(res);
        }
      },
      error: err => {
        this.loading = false;
        if (err.status === 501) {
          this.errorMessage = 'Not yet implemented for this platform.';
        } else if (err.status === 401) {
          this.errorMessage = 'Token expired — please re-authorize.';
        } else {
          this.errorMessage = err.error?.message || 'An error occurred fetching analytics.';
        }
      }
    });
  }

  private applyResult(res: any): void {
    this.result = { ...res, platform: this.selectedPlatform };
    this.thumbnailUrl = res.thumbnailUrl ?? null;
    this.videoUrl = res.shareUrl ?? null;
    this.fromCache = res.fromCache ?? false;
    this.lastRefresh = res.lastRefresh ? new Date(res.lastRefresh) : new Date();
    this.buildChart(res.timeSeries ?? []);
  }

  /**
   * Instagram Graph media IDs cannot be derived from a web URL, but the shortcode in a /p/, /reel/
   * or /tv/ link can be resolved server-side — so send the shortcode when a link is pasted.
   */
  private contentIdForRequest(): string {
    const raw = this.videoId.trim();
    if (!this.isInstagram) return raw;

    const match = raw.match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/i);
    return match ? match[1] : raw;
  }

  private getDateRange(): { startDate?: string; endDate?: string } {
    if (!this.timeframeSupported || this.selectedTimeframeDays === null) return {};
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - this.selectedTimeframeDays);
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    return { startDate: fmt(start), endDate: fmt(end) };
  }

  // ── Stories ───────────────────────────────────────────────────────────────────

  loadStories(): void {
    if (!this.selectedOAuthId) return;

    this.storiesLoading = true;
    this.storiesError = '';
    this.storyGroups = [];
    this.selectedStoryGroup = null;
    this.selectedStoryFrame = null;

    this.oauthService.getInstagramStories(this.selectedOAuthId).subscribe({
      next: res => {
        this.storiesLoading = false;
        this.storiesLoaded = true;
        if (res.success === false) {
          this.storiesError = res.message || 'Could not load stories.';
          return;
        }
        this.storyGroups = res.groups ?? [];
        if (this.storyGroups.length) this.selectStoryGroup(this.storyGroups[0]);
      },
      error: err => {
        this.storiesLoading = false;
        this.storiesLoaded = true;
        this.storiesError = err.status === 401
          ? 'Token expired — please re-authorize.'
          : err.error?.message || 'An error occurred loading stories.';
      }
    });
  }

  selectStoryGroup(group: StoryGroup): void {
    this.selectedStoryGroup = group;
    this.selectedStoryFrame = null;
    this.buildStoryChart(group);
  }

  selectStoryFrame(frame: StoryFrame): void {
    this.selectedStoryFrame = this.selectedStoryFrame?.mediaId === frame.mediaId ? null : frame;
  }

  private buildStoryChart(group: StoryGroup): void {
    const labels = group.frames.map((_, i) => `Frame ${i + 1}`);
    const data = group.frames.map(f => this.frameViews(f) ?? 0);

    this.storyChartData = {
      labels,
      datasets: [{
        data,
        borderColor: '#4a90e2',
        backgroundColor: 'rgba(74,144,226,0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };
  }

  // Null when Instagram suppressed the view count — not the same thing as zero views.
  frameViews(frame: StoryFrame): number | null {
    const analytics = frame.analytics;
    if (!analytics?.success) return null;
    if (analytics.suppressedMetrics?.['views']) return null;
    return analytics.views ?? 0;
  }

  frameLabel(frame: StoryFrame): string {
    const index = (this.selectedStoryGroup?.frames.indexOf(frame) ?? 0) + 1;
    return `Frame ${index}`;
  }

  storyGroupLabel(group: StoryGroup): string {
    const posted = group.startedAt ? new Date(group.startedAt) : null;
    const when = posted
      ? posted.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : 'Unknown time';
    const frames = group.frames.length === 1 ? '1 frame' : `${group.frames.length} frames`;
    return `${when} · ${frames}`;
  }

  expiresIn(group: StoryGroup): string {
    if (!group.expiresAt) return '';
    const mins = Math.round((new Date(group.expiresAt).getTime() - Date.now()) / 60000);
    if (mins <= 0) return 'expired';
    if (mins < 60) return `expires in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `expires in ${hrs}h ${mins % 60}m`;
  }

  // Number of frames whose insights failed outright (not suppressed — genuinely unavailable).
  failedFrameCount(group: StoryGroup): number {
    return group.frames.filter(f => !f.analytics?.success).length;
  }

  // ── Breakdowns ────────────────────────────────────────────────────────────────

  private readonly navigationLabels: Record<string, string> = {
    tap_forward:   'Tap Forward',
    tap_back:      'Tap Back',
    tap_exit:      'Tap Exit',
    swipe_forward: 'Swipe Forward'
  };

  private readonly profileActivityLabels: Record<string, string> = {
    bio_link_clicked: 'Bio Link Taps',
    call:             'Calls',
    direction:        'Directions',
    email:            'Emails',
    text:             'Texts',
    other:            'Other'
  };

  navigationEntries(analytics: any): { key: string; label: string; value: number }[] {
    return this.breakdownEntries(analytics?.navigationBreakdown, this.navigationLabels);
  }

  profileActivityEntries(analytics: any): { key: string; label: string; value: number }[] {
    return this.breakdownEntries(analytics?.profileActivityBreakdown, this.profileActivityLabels);
  }

  private breakdownEntries(
    breakdown: Record<string, number> | undefined,
    labels: Record<string, string>
  ): { key: string; label: string; value: number }[] {
    if (!breakdown) return [];
    return Object.keys(breakdown).map(key => ({
      key,
      label: labels[key] ?? this.humanize(key),
      value: breakdown[key]
    }));
  }

  private humanize(key: string): string {
    return key.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  // ── Charts ────────────────────────────────────────────────────────────────────

  private buildChart(timeSeries: any[]): void {
    if (!timeSeries?.length) return;

    const allKeys = Object.keys(timeSeries[0]?.values ?? {});
    this.timeSeriesMetricOptions = allKeys;
    this.selectedTimeSeriesMetric = allKeys[0] ?? 'views';
    this.updateChartDataset(timeSeries, this.selectedTimeSeriesMetric);
    this.chartVisible = true;
  }

  onTimeSeriesMetricChange(): void {
    if (!this.result?.timeSeries?.length) return;
    this.updateChartDataset(this.result.timeSeries, this.selectedTimeSeriesMetric);
  }

  private updateChartDataset(timeSeries: any[], metricKey: string): void {
    const labels = timeSeries.map((p: any) => p.date);
    const data = timeSeries.map((p: any) => p.values?.[metricKey] ?? 0);
    this.chartData = {
      labels,
      datasets: [{
        data,
        borderColor: '#4a90e2',
        backgroundColor: 'rgba(74,144,226,0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5
      }]
    };
  }

  // ── Formatting ────────────────────────────────────────────────────────────────

  formatDuration(seconds: number): string {
    const totalSeconds = Math.round(seconds);
    const d = Math.floor(totalSeconds / 86400);
    const h = Math.floor((totalSeconds % 86400) / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  }

  formatMetricValue(metric: any): string {
    if (metric.status === 'suppressed') return '—';
    const v = metric.value ?? 0;
    switch (metric.format) {
      case 'percent':   return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
      case 'duration':  return this.formatDuration(v);
      case 'minutes':   return `${Math.round(v).toLocaleString()}m`;
      default:          return Math.round(v).toLocaleString();
    }
  }

  // Suppressed metrics get Instagram's own explanation as the tooltip.
  metricTooltip(metric: any): string {
    return metric.status === 'suppressed'
      ? `Suppressed by Instagram — ${metric.statusDetail || 'fewer than 5 viewers'}`
      : metric.description ?? '';
  }

  formatRatio(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  }

  formatCount(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return value.toLocaleString();
  }

  formatTime(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  }

  lastRefreshAgo(): string {
    if (!this.lastRefresh) return '';
    const mins = Math.round((Date.now() - this.lastRefresh.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins === 1) return '1 minute ago';
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.round(mins / 60);
    return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
  }

  timeSeriesMetricLabel(key: string): string {
    const labels: Record<string, string> = {
      views: 'Views',
      estimatedMinutesWatched: 'Watch Time (min)'
    };
    return labels[key] ?? key;
  }
}
